import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  InputLabel,
  Grid,
  Chip,
  Modal,
  Fade,
  Select,
  FormControl,
  Paper,
  Card,
  IconButton,
  Collapse,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  CardContent,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  DialogContentText,
  TablePagination,
  ButtonGroup,
  FormHelperText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from 'prop-types';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close'; // or use DeleteIcon for trash
import { set } from 'lodash';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone'; // Import timezone plugin
import utc from 'dayjs/plugin/utc'; // Import utc plugin to handle UTC

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BrokerList = () => {
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [brokerClientData, setBrokerClientData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  const [expandedBroker, setExpandedBroker] = useState({});
  const [newSelectedClient, setNewSelectedClient] = useState({});
  const [transactionData, setTransactionData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [effectiveDates, setEffectiveDates] = React.useState({});
  const [commissionEffectiveDates, setCommissionEffectiveDates] = React.useState({});
  const [brokerFilterType, setBrokerFilterType] = useState('currently');

  const [effectiveDateErrorText, setEffectiveDateErrorText] = React.useState('');
  const [commissionEffectiveDateErrorText, setCommissionEffectiveDateErrorText] = React.useState('');
  // const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [, setClientToDelete] = useState(null);
  const [clientFilterType, setClientFilterType] = useState('currently');
  const [brokerId, setBrokerId] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [brokerData, setBrokerData] = useState({});
  const [updateFormVisible, setUpdateFormVisible] = useState(false);
  const [clientCommissions, setClientCommissions] = useState({});
  const doNotPerformCustomerFiltration = localStorage.getItem('role') === 'Super Admin' || localStorage.getItem('role') === 'Employee';
  const loggedInUserBrokerID = localStorage.getItem('brokerID');

  dayjs.extend(timezone);
  dayjs.extend(utc);

  const handleEffectiveDateChange = (clientId, date) => {
    const dateInCST = dayjs(date).tz('America/Chicago', true);
    setEffectiveDates(prev => ({
      ...prev,
      [clientId]: dateInCST.format('YYYY-MM-DD'), // Format for backend
    }));
  };

  const handleCommissionEffectiveDateChange = (clientId, date) => {
    const dateInCST = dayjs(date).tz('America/Chicago', true);
    setCommissionEffectiveDates(prev => ({
      ...prev,
      [clientId]: dateInCST.format('YYYY-MM-DD'), // Format for backend
    }));
  };

  // const handleEffectiveDateChange = (date) => {
  //   console.log('Original Effective Date:', date);

  //   // Convert the selected date to CST (Central Standard Time)
  //   const dateInCST = dayjs(date).tz('America/Chicago', true); // CST timezone conversion

  //   console.log('Effective Date in CST:', dateInCST.format()); // Log the date in CST

  //   // Set the state with the CST formatted date
  //   setEffectiveDate(dateInCST);

  //   // Reset error text
  //   setEffectiveDateErrorText('');
  // };

  const getNextInvoiceCycleDate = () => {
    const today = dayjs().tz('America/Chicago'); // CST-aware current day
    const currentDay = today.day(); // 0 = Sunday, ..., 6 = Saturday

    let nextCycleDate;
    if (currentDay >= 4 || currentDay === 0) {
      // Thurs–Sun → next Monday
      const daysToNextMonday = (8 - currentDay) % 7 || 7;
      nextCycleDate = today.add(daysToNextMonday, 'day');
    } else {
      // Mon–Wed → next Thursday
      const daysToNextThursday = (11 - currentDay) % 7 || 7;
      nextCycleDate = today.add(daysToNextThursday, 'day');
    }

    return nextCycleDate.startOf('day'); // Still in CST
  };

  // const [effectiveDate, setEffectiveDate] = React.useState(getNextInvoiceCycleDate());

  const sharedFormStyles = {
    '& .MuiInputBase-root': {
      height: '32px', // Match TextField height
      minHeight: '32px',
    },
    '& .MuiInputBase-input': {
      fontSize: '11px', // Match font size
      padding: '4px 8px',
    },
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Fetch brokers and clients
      const [brokersResponse, clientsResponse] = await Promise.all([fetch(`${BACKEND_URL}/v1/brokers`), fetch(`${BACKEND_URL}/v1/client`)]);

      if (!brokersResponse.ok || !clientsResponse.ok) {
        const errorText = !brokersResponse.ok ? await brokersResponse.text() : await clientsResponse.text();
        throw new Error(`Error fetching data: ${errorText}`);
      }

      const brokersData = await brokersResponse.json();
      let clientsData = await clientsResponse.json();

      const filteredBrokers = doNotPerformCustomerFiltration
         ? brokersData.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
         : brokersData.filter(b => b.id === loggedInUserBrokerID).sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));
      setBrokers(filteredBrokers);

      clientsData = clientsData.filter(client => client.clientID !== '100').sort((a, b) => Number(a.clientID) - Number(b.clientID));
      setClients(clientsData);

      // Step 3: Fetch broker-clients
      const newSelectedClientData = {};
      const newBrokerData = {};
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // JS months start from 0

      const brokerClientResults = await Promise.all(
        filteredBrokers.map(async broker => {
          const response = await fetch(`${BACKEND_URL}/v1/broker/${broker.id}/clients`);
          const clients = response.ok ? await response.json() : [];
          newSelectedClientData[broker.id] = clients;
          return { brokerId: broker.id, clients };
        })
      );

      const normalize = data => {
        if (typeof data === 'number') return data;
        if (typeof data === 'string') return parseFloat(data) || 0;
        if (typeof data === 'object') {
          return parseFloat(data?.quantity || data?.volume || data?.total || 0);
        }
        return 0;
      };

      // Step 4: Fetch all metrics
      const fetchVolumeAndCommissionPromises = [];

      for (const broker of filteredBrokers) {
        const clients = newSelectedClientData[broker.id] || [];

        for (const client of clients) {
          const clientId = client.clientID;
          newBrokerData[clientId] = {};

          const fetchBoth = async (type, extra = '') => {
            const [qtyRes, comRes] = await Promise.all([fetch(`${BACKEND_URL}/v1/client/${broker.id}/${clientId}/quantity?type=${type}${extra}`), fetch(`${BACKEND_URL}/v1/${broker.id}/commissions?type=${type}${extra}`)]);

            const qtyData = qtyRes.ok ? await qtyRes.json() : {};
            const comData = comRes.ok ? await comRes.json() : {};

            return {
              volume: normalize(qtyData),
              commission: normalize(comData),
            };
          };

          fetchVolumeAndCommissionPromises.push(
            Promise.all([
              fetchBoth('total').then(({ volume, commission }) => {
                newBrokerData[clientId].totalVolume = volume;
                newBrokerData[clientId].totalCommission = commission;
              }),
              fetchBoth('currently').then(({ volume, commission }) => {
                newBrokerData[clientId].currentVolume = volume;
                newBrokerData[clientId].currentCommission = commission;
              }),
              fetchBoth('weekly', `&week=${getISOWeek(currentDate)}&year=${currentYear}`).then(({ volume, commission }) => {
                newBrokerData[clientId].weeklyVolume = volume;
                newBrokerData[clientId].weeklyCommission = commission;
              }),
              fetchBoth('monthly', `&month=${String(currentMonth).padStart(2, '0')}&year=${currentYear}`).then(({ volume, commission }) => {
                newBrokerData[clientId].monthlyVolume = volume;
                newBrokerData[clientId].monthlyCommission = commission;
              }),
              fetchBoth('yearly', `&year=${currentYear}`).then(({ volume, commission }) => {
                newBrokerData[clientId].yearlyVolume = volume;
                newBrokerData[clientId].yearlyCommission = commission;
              }),
            ])
          );
        }
      }

      await Promise.all(fetchVolumeAndCommissionPromises);

      setNewSelectedClient(newSelectedClientData);
      setBrokerData(newBrokerData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  function getISOWeek(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  }

  // Ensure fetchData is called in useEffect
  useEffect(() => {
    fetchData();
  }, []);

  const fetchBrokers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/v1/brokers`);
      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Error fetching brokers: ${response.status} ${response.statusText} - ${errorMessage}`);
      }
      const data = await response.json();

      // Apply filtration if required
      const filteredBrokers = doNotPerformCustomerFiltration
        ? data
        : data.filter(broker => broker.id === loggedInUserBrokerID);

      // Sort brokers by ID in ascending order
      const sortedBrokers = filteredBrokers.sort((a, b) => a.id - b.id);

      setBrokers(sortedBrokers);
    } catch (error) {
      console.error('Error fetching brokers:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignClients = brokerId => {
    setFormVisible(true);
    setUpdateFormVisible(false);
    setBrokerId(brokerId);
  };

  const handleSelectChange = event => {
    const newSelected = event.target.value;

    // Keep existing commissions
    const updatedCommissions = { ...clientCommissions };
    newSelected.forEach(clientID => {
      if (!updatedCommissions[clientID]) {
        updatedCommissions[clientID] = '0.00'; // Default value
      }
    });

    setSelectedClients(newSelected);
    setClientCommissions(updatedCommissions);
  };

  const handleBrokerChange = async brokerId => {
    setBrokerId(brokerId);
    const broker = brokers.find(b => b.id === brokerId);
    if (broker) {
      const assignedClients = broker.clients || [];

      // Extract assigned client IDs
      const assignedClientIDs = assignedClients.map(c => c.clientID);

      // Extract commission map from the broker's clients
      const commissionMap = {};
      const effectiveDateMap = {};
      const commissionEffectiveDateMap = {};
      assignedClients.forEach(c => {
        commissionMap[c.clientID] = c.commission.toFixed(2); // Convert to percentage
        if (c.effectiveDate) {
          effectiveDateMap[c.clientID] = c.effectiveDate;
        }
        if (c.commissionEffectiveDate) {
          commissionEffectiveDateMap[c.clientID] = c.commissionEffectiveDate;
        }
      });

      setSelectedClients(assignedClientIDs);
      setClientCommissions(commissionMap);
      setEffectiveDates(effectiveDateMap);
      setCommissionEffectiveDates(commissionEffectiveDateMap);
    }
  };

  const fetchBrokerClients = async brokerId => {
    console.log('Broker id: ', brokerId);
    try {
      const response = await fetch(`${BACKEND_URL}/v1/broker/${brokerId}/clients`);
      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Error fetching broker clients: ${response.status} ${response.statusText} - ${errorMessage}`);
      }
      const brokerClients = await response.json();
      // setBrokerClientData(brokerClients);

      // Set full client data
      // setClients(brokerClients);

      // Set selectedClients to their IDs
      const selectedClientIDs = brokerClients.map(client => client.clientID);
      setSelectedClients(selectedClientIDs);

      return brokerClients.length === 0 ? [] : brokerClients;
    } catch (error) {
      console.error('Error fetching broker clients:', error);
      alert('An error occurred while fetching broker clients.');
      // setSnackbarOpen(true);
      throw error;
    }
  };

  const handleCommissionChange = (clientID, value) => {
    setClientCommissions(prev => ({
      ...prev,
      [clientID]: value,
    }));
  };

  const handleAssignClient = async () => {
    if (!brokerId || selectedClients.length === 0) {
      alert('Please select both a broker and at least one client.');
      return;
    }

    const missingData = selectedClients.some(clientId => !clientCommissions[clientId] || parseFloat(clientCommissions[clientId]) <= 0 || !effectiveDates[clientId] || !commissionEffectiveDates[clientId]);

    if (missingData) {
      alert('Please enter a valid commission, commission effective date and effective date for all selected clients.');
      return;
    }

    try {
      const brokersResponse = await fetch(`${BACKEND_URL}/v1/brokers`);
      const allBrokers = await brokersResponse.json();

      const assignedToOtherBroker = selectedClients.filter(clientId => allBrokers.some(broker => broker.id !== brokerId && Array.isArray(broker.clients) && broker.clients.some(client => client.clientID === clientId)));

      if (assignedToOtherBroker.length > 0) {
        alert('One or more clients are already assigned to another broker!');
        return;
      }

      const clientAssignmentMap = selectedClients.reduce((map, clientId) => {
        const commission = parseFloat(clientCommissions[clientId]);
        const effectiveDate = effectiveDates[clientId];
        const commissionEffectiveDate = commissionEffectiveDates[clientId];
        if (!isNaN(commission) && commission > 0 && effectiveDate && commissionEffectiveDate) {
          map[clientId] = {
            commission,
            effectiveDate,
            commissionEffectiveDate,
          };
        }
        return map;
      }, {});

      const payload = { clientAssignmentMap };

      console.log('Payload for backend:', payload);

      const response = await fetch(`${BACKEND_URL}/v1/broker/${brokerId}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Error assigning clients: ${response.status} ${response.statusText} - ${errorMessage}`);
      }

      await fetchBrokers();
      alert('Clients assigned successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Error assigning clients:', error);
      setError(error.message);
      alert('An error occurred while assigning the clients.');
    }
  };

  const handleRemoveClient = clientID => {
    const client = clients.find(c => c.clientID === clientID);
    const confirmRemove = window.confirm(`Are you sure you want to remove client "${client.clientName}" from the selection?`);

    if (confirmRemove) {
      setSelectedClients(prev => prev.filter(id => id !== clientID));
      setClientCommissions(prev => {
        const updated = { ...prev };
        delete updated[clientID];
        return updated;
      });

      setEffectiveDates(prev => {
        const updated = { ...prev };
        delete updated[clientID];
        return updated;
      });

      setCommissionEffectiveDates(prev => {
        const updated = { ...prev };
        delete updated[clientID];
        return updated;
      });

      // console.log('Final Selected Clients:', selectedClients);
      // console.log('Final Client Commissions:', clientCommissions);
    }
  };

  const handleUpdateBroker = () => {
    setUpdateFormVisible(true);
    setFormVisible(false);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedBroker || !selectedBroker.id) {
      alert('Please select a broker to update.');
      // setSnackbarOpen(true);
      return;
    }

    // handleAssignClient(selectedBroker.id);

    try {
      const response = await fetch(`${BACKEND_URL}/v1/brokers/${selectedBroker.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedBroker),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Error updating broker: ${response.status} ${response.statusText} - ${errorMessage}`);
      }

      await response.json();

      alert('Broker updated successfully!');
      // setSnackbarOpen(true);
      setUpdateFormVisible(false);
      fetchBrokers();
      window.location.reload();
    } catch (error) {
      console.error('Error updating broker:', error);
      alert(error.message || 'Failed to update broker');
      // setSnackbarOpen(true);
    }
  };

  const handleExpandClick = async brokerId => {
    setExpandedBroker(prevState => ({
      ...prevState,
      [brokerId]: !prevState[brokerId],
    }));

    // If clients for this broker are already fetched, no need to fetch again
    if (!newSelectedClient[brokerId]) {
      try {
        const response = await fetch(`${BACKEND_URL}/v1/broker/${brokerId}/clients`);
        if (!response.ok) throw new Error('Failed to fetch clients');
        const clients = await response.json();

        setNewSelectedClient(prevClients => ({
          ...prevClients,
          [brokerId]: clients,
        }));

        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();

        for (const client of clients) {
          const clientId = client.clientID;
          const totalVolumeResponse = await fetch(`${BACKEND_URL}/v1/client/${brokerId}/${clientId}/quantity?type=total`);
          const monthlyVolumeResponse = await fetch(`${BACKEND_URL}/v1/client/${brokerId}/${clientId}/quantity?type=monthly&month=${currentMonth}&year=${currentYear}`);
          const totalCommissionResponse = await fetch(`${BACKEND_URL}/v1/${brokerId}/commissions?type=total`);
          const monthlyCommissionResponse = await fetch(`${BACKEND_URL}/v1/${brokerId}/commissions?type=monthly`);

          if (!totalVolumeResponse.ok || !monthlyVolumeResponse.ok || !totalCommissionResponse.ok || !monthlyCommissionResponse.ok) {
            throw new Error('Failed to fetch one or more data sets');
          }

          const totalVolume = await totalVolumeResponse.json();
          const monthlyVolume = await monthlyVolumeResponse.json();
          const totalCommission = await totalCommissionResponse.json();
          const monthlyCommission = await monthlyCommissionResponse.json();

          const normalizeVolume = value => {
            if (typeof value === 'number') return value;
            if (typeof value === 'string') return parseFloat(value) || 0;
            if (value && typeof value === 'object') {
              if ('volume' in value) return parseFloat(value.volume) || 0;
              if ('monthly' in value) return parseFloat(value.monthly) || 0;
            }
            return 0;
          };

          setBrokerData(prevData => ({
            ...prevData,
            [clientId]: {
              totalVolume: normalizeVolume(totalVolume.quantity),
              monthlyVolume: normalizeVolume(monthlyVolume.quantity),
              totalCommission: normalizeVolume(totalCommission.total),
              monthlyCommission: normalizeVolume(monthlyCommission.monthly),
              earnedCommissionUSD: client.earnedCommissionUSD || 0,
              earnedCommissionCAD: client.earnedCommissionCAD || 0,
            },
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      }
    }
  };

  const aggregateBrokerMetrics = brokerId => {
    const clients = newSelectedClient[brokerId] || [];
    // const GALLONS_TO_LITRES = 3.78541;
    const currentDate = new Date(); // June 11, 2025

    let totalVolume = 0;
    let totalCommission = 0;
    let earnedCommissionCAD = 0;

    const parseVolume = value => {
      const num = parseFloat(value) || 0;
      // if (num > 1000) {
      //   return num / GALLONS_TO_LITRES; // Convert litres to gallons
      // }
      return num; // Already in gallons
    };

    // Aggregate metrics across all clients for this broker
    clients.forEach(client => {
      const clientData = brokerData[client.clientID] || {};
      const effectiveDate = client.effectiveDate ? new Date(client.effectiveDate) : null;

      // Log raw client data for debugging
      console.log(`Client ${client.clientID} Data for Broker ${brokerId}:`, {
        currentVolume: clientData.currentVolume,
        currentCommission: clientData.currentCommission,
        weeklyVolume: clientData.weeklyVolume,
        weeklyCommission: clientData.weeklyCommission,
        monthlyVolume: clientData.monthlyVolume,
        monthlyCommission: clientData.monthlyCommission,
        yearlyVolume: clientData.yearlyVolume,
        yearlyCommission: clientData.yearlyCommission,
        totalVolume: clientData.totalVolume,
        totalCommission: clientData.totalCommission,
        effectiveDate: client.effectiveDate,
        commission: client.commission,
      });

      let clientVolume = 0;
      let clientCommission = 0;

      if (effectiveDate) {
        // Define time boundaries (same as client table)
        const truncateToDate = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayOfWeek = currentDate.getDay();
        const currentWeekStart = new Date(currentDate);
        currentWeekStart.setDate(currentDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const currentWeekEnd = new Date(currentWeekStart);
        currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
        const lastWeekStart = new Date(currentWeekStart);
        lastWeekStart.setDate(currentWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(currentWeekStart);
        lastWeekEnd.setDate(currentWeekStart.getDate() - 1);

        // Apply filter logic (aligned with client table)
        switch (brokerFilterType) {
          case 'currently':
            if (truncateToDate(effectiveDate) <= currentWeekEnd) {
              clientVolume = parseVolume(clientData.currentVolume);
              clientCommission = parseVolume(clientData.currentCommission) || clientVolume;
            }
            break;
          case 'weekly':
            if (truncateToDate(effectiveDate) <= lastWeekEnd) {
              clientVolume = parseVolume(clientData.weeklyVolume);
              clientCommission = parseVolume(clientData.weeklyCommission) || clientVolume;
            }
            break;
          case 'monthly':
            if (effectiveDate.getFullYear() <= currentDate.getFullYear()) {
              clientVolume = parseVolume(clientData.monthlyVolume);
              clientCommission = parseVolume(clientData.monthlyCommission) || clientVolume;
              if (clientVolume === 0) {
                clientVolume = parseVolume(clientData.weeklyVolume) * 4;
                clientCommission = clientVolume;
              }
            }
            break;
          case 'yearly':
            if (effectiveDate.getFullYear() <= currentDate.getFullYear()) {
              clientVolume = parseVolume(clientData.yearlyVolume);
              clientCommission = parseVolume(clientData.yearlyCommission) || clientVolume;
              if (clientVolume === 0) {
                const monthlyVol = parseVolume(clientData.monthlyVolume) || parseVolume(clientData.weeklyVolume) * 4;
                clientVolume = monthlyVol * 12;
                clientCommission = clientVolume;
              }
            }
            break;
          default:
            clientVolume = parseVolume(clientData.totalVolume);
            clientCommission = parseVolume(clientData.totalCommission) || clientVolume;
            break;
        }
      }

      // Convert gallons to litres and aggregate
      const clientVolumeLitres = clientVolume;
      const clientCommissionLitres = clientCommission;
      const clientEarnedCommissionCAD = clientCommissionLitres * (client.commission || 0);

      totalVolume += clientVolumeLitres;
      totalCommission += clientCommissionLitres;
      earnedCommissionCAD += clientEarnedCommissionCAD;

      // Log computed values for this client
      console.log(`Client ${client.clientID} Computed for Broker ${brokerId}:`, {
        filterType: brokerFilterType,
        clientVolume,
        clientCommission,
        clientVolumeLitres,
        clientCommissionLitres,
        commission: client.commission,
        clientEarnedCommissionCAD,
      });
    });

    // Log final aggregated metrics
    console.log(`Broker ${brokerId} Metrics for ${brokerFilterType}:`, {
      totalVolumeLitres: totalVolume,
      totalCommission,
      earnedCommissionCAD,
    });

    return {
      totalVolumeLitres: totalVolume,
      totalCommission: totalCommission,
      earnedCommissionCAD: earnedCommissionCAD,
    };
  };

  const aggregateAllBrokersMetrics = () => {
    return brokers.reduce(
      (acc, broker) => {
        const metrics = aggregateBrokerMetrics(broker.id);
        return {
          totalMonthlyVolume: metrics.totalMonthlyVolume || 0,
          totalMonthlyCommission: metrics.totalMonthlyCommission || 0,
          totalVolumeLitres: acc.totalVolumeLitres + metrics.totalVolumeLitres || 0,
          totalCommission: acc.totalVolumeLitres + metrics.totalVolumeLitres || 0,
          earnedCommissionUSD: acc.earnedCommissionUSD + (metrics.earnedCommissionUSD || 0),
          earnedCommissionCAD: acc.earnedCommissionCAD + (metrics.earnedCommissionCAD || 0),
        };
      },
      {
        totalMonthlyVolume: 0,
        totalMonthlyCommission: 0,
        totalVolumeLitres: 0,
        totalCommission: 0,
        earnedCommissionUSD: 0,
        earnedCommissionCAD: 0,
      }
    );
  };

  const handleDelete = async broker => {
    const isConfirmed = window.confirm('Are you sure you want to delete this broker?');
    if (isConfirmed) {
      try {
        const response = await fetch(`${BACKEND_URL}/v1/send-broker-deletion-approval-email?brokerId=${broker.id}`, {
          method: 'POST',
        });
        if (!response.ok) throw new Error('Failed to send approval email');
        const data = await response.json();
        alert(data.message);
        // setSnackbarOpen(true);
        window.alert('Approval email sent successfully');
      } catch (error) {
        alert('Failed to send approval email. Please try again.');
        // setSnackbarOpen(true);
        window.alert('Failed to send approval email');
      }
    }
  };

  const handleClientDelete = async (brokerId, clientId) => {
    const confirmed = window.confirm('Are you sure you want to delete this client?');
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/v1/${brokerId}/clients/${clientId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setNewSelectedClient(prevClients => ({
          ...prevClients,
          [brokerId]: prevClients[brokerId].filter(client => client.clientID !== clientId),
        }));
        alert('Client removed successfully!');
        // setSnackbarOpen(true);
      } else if (response.status === 404) {
        alert('Client or Broker not found.');
        // setSnackbarOpen(true);
      } else {
        const errorMessage = await response.text();
        throw new Error(`Error deleting client: ${response.status} ${response.statusText} - ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      setError('Error deleting client');
    } finally {
      setClientToDelete(null);
    }
  };

  const handleTransactionExpandClick = async (brokerId, clientId) => {
    if (transactionData[clientId]) {
      setTransactionData(prev => ({ ...prev, [clientId]: null }));
    } else {
      try {
        const response = await fetch(`${BACKEND_URL}/v1/transactions/companyXRef/${clientId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch transactions');
        }
        const transactions = await response.json();
        setTransactionData(prev => ({ ...prev, [clientId]: transactions }));
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setError('Error fetching transactions: ' + error.message);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedBrokers = brokers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box p={3}>
      <Modal
        open={loading}
        aria-labelledby="loading-modal-title"
        closeAfterTransition
        sx={{
          pointerEvents: 'none', // Makes the modal non-blocking
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start', // Aligns the banner at the top
          paddingTop: '20px',
          backgroundColor: 'transparent', // No overlay, table remains fully visible
        }}
      >
        <Fade in={loading} timeout={400}>
          <Box
            sx={{
              backgroundColor: 'rgba(49, 71, 143, 0.95)', // Semi-transparent primary color
              padding: '12px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: '90%',
            }}
          >
            <Typography
              id="loading-modal-title"
              variant="body1"
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#ffffff',
                fontFamily: 'sans-serif',
                textAlign: 'center',
              }}
            >
              Loading Data, Please Wait...
            </Typography>
          </Box>
        </Fade>
      </Modal>

      {/* Show loading or error only when neither form is visible */}
      {!formVisible && !updateFormVisible && loading && !brokers.length && <CircularProgress />}
      {!formVisible && !updateFormVisible && !loading && error && <Typography color="error">{error}</Typography>}

      {/* Header and buttons - hidden when either form is visible */}
      {!formVisible && !updateFormVisible && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography
            variant="h5"
            mt={4}
            // mb={2}
            sx={{
              fontWeight: 'bold',
              fontSize: '14px',
              fontFamily: 'sans-serif',
              textTransform: 'uppercase',
            }}
          >
            Brokers List
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            {doNotPerformCustomerFiltration && (
              <Button
                variant="contained"
                onClick={handleUpdateBroker}
                sx={{
                  padding: '2px 6px',
                  backgroundColor: '#31478F',
                  fontFamily: 'sans-serif',
                  fontSize: '10px',
                  marginRight: '10px',
                  '&:hover': {
                    backgroundColor: '#31478F', // Keeps the same color on hover
                  },
                }}
              >
                Update Broker
              </Button>
            )}
            {doNotPerformCustomerFiltration && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleAssignClients(1)}
                style={{
                  fontFamily: 'sans-serif',
                  fontSize: '10px',
                  height: '22px',
                  backgroundColor: '#31478F',
                  color: 'white',
                  marginRight: '10px',
                }}
              >
                Manage Clients
              </Button>
            )}
            {doNotPerformCustomerFiltration && (
              <Button
                variant="contained"
                onClick={() => navigate('/manage-brokers/BrokerReport')}
                sx={{
                  padding: '2px 6px',
                  backgroundColor: '#31478F',
                  fontFamily: 'sans-serif',
                  fontSize: '10px',
                  marginRight: '10px',
                  // marginLeft: '10px',
                  '&:hover': {
                    backgroundColor: '#31478F', // Keeps the same color on hover
                  },
                }}
              >
                Broker Report
              </Button>
            )}
            {doNotPerformCustomerFiltration && (
              <Button
                variant="contained"
                onClick={() => navigate('/manage-brokers/BrokerCommission')}
                sx={{
                  padding: '2px 6px',
                  backgroundColor: '#31478F',
                  fontFamily: 'sans-serif',
                  fontSize: '10px',
                  marginRight: '10px',
                  '&:hover': {
                    backgroundColor: '#31478F', // Keeps the same color on hover
                  },
                }}
              >
                Broker Commission
              </Button>
            )}
            {doNotPerformCustomerFiltration && (
              <Button
                variant="contained"
                onClick={() => navigate('/manage-brokers/BrokerUsers')}
                sx={{
                  padding: '2px 6px',
                  backgroundColor: '#31478F',
                  fontFamily: 'sans-serif',
                  fontSize: '10px',
                  marginRight: '10px',
                  '&:hover': {
                    backgroundColor: '#31478F', // Keeps the same color on hover
                  },
                }}
              >
                Broker User
              </Button>
            )}
            {doNotPerformCustomerFiltration && (
              <Button
                variant="contained"
                onClick={() => navigate('/manage-brokers/BrokerOnboard')}
                sx={{
                  padding: '2px 6px',
                  backgroundColor: '#31478F',
                  fontFamily: 'sans-serif',
                  fontSize: '10px',
                  marginRight: '10px',
                  '&:hover': {
                    backgroundColor: '#31478F', // Keeps the same color on hover
                  },
                }}
              >
                Onboard Broker
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Assign Clients Form - shown only when formVisible is true */}
      {formVisible && (
        <Box>
          {/* <Card
            variant="outlined"
            sx={{ width: "800px", padding: 3, justifyContent: "center" }}
          > */}
          <CardContent>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                fontFamily: 'sans-serif',
                fontSize: '14px',
                textTransform: 'uppercase',
                marginTop: '-30px',
                marginBottom: '20px',
                textTransform: 'uppercase',
              }}
            >
              Manage Broker Clients
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" sx={sharedFormStyles}>
                  <InputLabel id="brokerDropdown">Select Broker</InputLabel>
                  {loading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        display: 'block',
                        textTransform: 'uppercase',
                      }}
                    />
                  ) : error ? (
                    <Typography color="error" align="center">
                      {`Error: ${error}`}
                    </Typography>
                  ) : (
                    <Select
                      labelId="brokerDropdown"
                      id="brokerDropdown"
                      value={brokerId}
                      label="Select Broker"
                      onChange={e => handleBrokerChange(e.target.value)}
                      disabled={brokers.length === 0}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: '200px',
                            minHeight: '100px',
                            textTransform: 'uppercase',
                            '& .MuiMenuItem-root': {
                              fontSize: '10px',
                              marginLeft: '5px',
                              padding: '10px 15px',
                            },
                          },
                        },
                      }}
                    >
                      {brokers.map(broker => (
                        <MenuItem key={broker.id} value={broker.id}>
                          {`${broker.id} - ${broker.brokerName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" sx={sharedFormStyles}>
                  <InputLabel
                    id="clientDropdown"
                    sx={{
                      fontSize: '15px',
                      // top: '-20%', // Initially center the label vertically
                    }}
                  >
                    Select Client
                  </InputLabel>
                  {loading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        display: 'block',
                      }}
                    />
                  ) : error ? (
                    <Typography color="error" align="center">
                      {`Error: ${error}`}
                    </Typography>
                  ) : (
                    <Select
                      labelId="clientDropdown"
                      id="clientDropdown"
                      value={selectedClients}
                      label="Select Client"
                      multiple
                      onChange={handleSelectChange}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: '200px',
                            minHeight: '100px',
                            '& .MuiMenuItem-root': {
                              fontSize: '10px',
                              marginLeft: '5px',
                              padding: '5px 15px',
                            },
                          },
                        },
                      }}
                    >
                      {clients.map(client => (
                        <MenuItem key={client.clientID} value={client.clientID}>
                          {`${client.clientID} - ${client.clientName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={15}>
                {selectedClients.length > 0 && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      fontFamily: 'sans-serif',
                      paddingBottom: '15px',
                    }}
                  >
                    SELECTED CLIENTS:
                  </Typography>
                )}
                {/* <div>
                  {selectedClients.map(clientID => {
                    const client = clients.find(client => client.clientID === clientID);
                    return (
                      client && (
                        <Chip
                          key={client.clientID}
                          label={`${client.clientID} - ${client.clientName}`}
                          sx={{ margin: '4px' }}
                        />
                      )
                    );
                  })}
                </div> */}

                <Grid container spacing={1}>
                  {selectedClients.map(clientID => {
                    const client = clients.find(c => c.clientID === clientID);
                    return (
                      client && (
                        <React.Fragment key={client.clientID}>
                          <Grid item xs={3}>
                            <TextField
                              label="Client Name"
                              value={`${client.clientID} - ${client.clientName}`}
                              fullWidth
                              size="small"
                              disabled
                              InputProps={{
                                sx: { fontSize: 15 },
                              }}
                              inputProps={{
                                sx: {
                                  whiteSpace: 'nowrap',
                                  // overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontSize: 10,
                                  // width: '150px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              label="Commission (%)"
                              type="number"
                              value={clientCommissions[clientID] || '0.0000'}
                              // onChange={e => handleCommissionChange(clientID, e.target.value)}
                              onChange={e => {
                                const value = parseFloat(e.target.value);
                                const safeValue = isNaN(value) || value < 0 ? 0.0000 : value;
                                handleCommissionChange(clientID, safeValue.toFixed(4));
                              }}
                              // fullWidth
                              size="small"
                              disabled={!!clientCommissions[clientID] && clientCommissions[clientID] !== '0.00'}
                              inputProps={{
                                step: '0.0001',
                                min: '0.0000',
                                style: { fontSize: 11 },
                              }}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <FormControl
                              // sx={{ minWidth: '100%' }}
                              size="small"
                              error={effectiveDateErrorText}
                            >
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  placeholder="Effective Date" // Use placeholder instead of label
                                  label="Client Effective Date"
                                  // value={dayjs(effectiveDates[clientID])}
                                  value={effectiveDates[clientID] && dayjs(effectiveDates[clientID]).isValid() ? dayjs(effectiveDates[clientID]) : null}
                                  onChange={date => handleEffectiveDateChange(clientID, date)}
                                  size="small"
                                  disabled={!!effectiveDates[clientID]} // Disable if value exists
                                  slotProps={{
                                    textField: {
                                      InputLabelProps: {
                                        sx: effectiveDates[clientID] ? { fontSize: '13px' } : { fontSize: '10px', top: '-9px' },
                                      },
                                      sx: {
                                        '& .MuiInputBase-root': {
                                          height: 30, // Increased height for better centering
                                          minHeight: 30,
                                          // width: '150px',
                                          // paddingRight: '28px', // Space for the icon
                                          display: 'flex',
                                          alignItems: 'center', // Ensures vertical centering
                                        },
                                        '& .MuiInputBase-input': {
                                          fontSize: 11,
                                          textAlign: 'left', // Centers text horizontally
                                          padding: '8px 5px', // Centers text vertically
                                        },
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 12, // Reduce icon size
                                          right: '8px', // Position the icon
                                        },
                                      },
                                    },
                                  }}
                                  // onChange={handleEffectiveDateChange}
                                  // shouldDisableDate={(date) => {
                                  //   const day = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                                  //   const today = dayjs().startOf('day'); // Get today without time
                                  //   return date.isBefore(today); // Allow only Monday (1) & Thursday (4) and disable past dates
                                  // }}
                                />
                              </LocalizationProvider>

                              {effectiveDateErrorText && <FormHelperText id="input-error-text">{effectiveDateErrorText}</FormHelperText>}
                            </FormControl>
                          </Grid>

                          <Grid item xs={2}>
                            <FormControl sx={{ minWidth: '100%' }} size="small" error={commissionEffectiveDateErrorText}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  placeholder="Commission Effective Date" // Use placeholder instead of label
                                  label="Commission Effective Date"
                                  // value={dayjs(commissionEffectiveDates[clientID])}
                                  value={commissionEffectiveDates[clientID] && dayjs(commissionEffectiveDates[clientID]).isValid() ? dayjs(commissionEffectiveDates[clientID]) : null}
                                  onChange={date => handleCommissionEffectiveDateChange(clientID, date)}
                                  size="small"
                                  disabled={!!commissionEffectiveDates[clientID]}
                                  slotProps={{
                                    textField: {
                                      InputLabelProps: {
                                        sx: commissionEffectiveDates[clientID] ? { fontSize: '13px' } : { fontSize: '10px', top: '-9px' },
                                      },
                                      sx: {
                                        '& .MuiInputBase-root': {
                                          height: 30, // Increased height for better centering
                                          minHeight: 30,
                                          width: '160px',
                                          // paddingRight: '48px', // Space for the icon
                                          display: 'flex',
                                          alignItems: 'center', // Ensures vertical centering
                                        },
                                        '& .MuiInputBase-input': {
                                          fontSize: 11,
                                          textAlign: 'left', // Centers text horizontally
                                          padding: '8px 5px', // Centers text vertically
                                        },
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 12, // Reduce icon size
                                          // right: '8px', // Position the icon
                                        },
                                      },
                                    },
                                  }}
                                  // onChange={handleEffectiveDateChange}
                                  // shouldDisableDate={(date) => {
                                  //   const day = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                                  //   const today = dayjs().startOf('day'); // Get today without time
                                  //   return date.isBefore(today); // Allow only Monday (1) & Thursday (4) and disable past dates
                                  // }}
                                />
                              </LocalizationProvider>

                              {commissionEffectiveDateErrorText && <FormHelperText id="input-error-text">{commissionEffectiveDateErrorText}</FormHelperText>}
                            </FormControl>
                          </Grid>

                          <Grid item xs={3}>
                            <IconButton size="small" disableRipple disableFocusRipple disableTouchRipple onClick={() => handleRemoveClient(clientID)} sx={{ marginTop: '2px', paddingRight: '300px' }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </React.Fragment>
                      )
                    );
                  })}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Button
                  style={{
                    marginTop: '15px',
                    fontFamily: 'sans-serif',
                    fontSize: '10px',
                    backgroundColor: '#31478F',
                    marginLeft: '190px',
                  }}
                  variant="contained"
                  onClick={handleAssignClient}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#31478F', // Prevent hover effect by keeping the same color
                    },
                  }}
                >
                  Assign Client
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  // onClick={() => setFormVisible(false)}
                  onClick={() => {
                    setFormVisible(false);
                    setSelectedClients([]); // Clear selected clients
                    setClientCommissions({}); // Clear commission values
                  }}
                  sx={{
                    mt: 2,
                    ml: 2,
                    backgroundColor: '#E8F0FE',
                    marginTop: '15px',
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
          {/* </Card> */}
        </Box>
      )}

      {/* Update Broker Form - shown only when updateFormVisible is true */}
      {updateFormVisible && (
        <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
          {/* <Card variant="outlined" sx={{ width: "800px", padding: 3 }}> */}
          <CardContent>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontFamily: 'sans-serif',
                fontSize: '14px',
                marginTop: '-30px',
                textTransform: 'uppercase',
                marginBottom: '20px',
              }}
            >
              Update Broker Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                >
                  <InputLabel
                    id="updateBrokerDropdown"
                    sx={{
                      fontSize: '11px',
                      top: '-30%', // Initially center the label vertically
                    }}
                  >
                    Select Broker
                  </InputLabel>
                  <Select
                    labelId="updateBrokerDropdown"
                    id="updateBrokerDropdown"
                    value={selectedBroker ? selectedBroker.id : ''}
                    label="Select Broker"
                    onChange={async e => {
                      const broker = brokers.find(b => b.id === e.target.value);
                      setSelectedBroker(broker);
                      await fetchBrokerClients(broker.id);
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: '200px',
                          minHeight: '100px',
                          '& .MuiMenuItem-root': {
                            fontSize: '10px',
                            marginLeft: '5px',
                            padding: '5px 15px',
                          },
                        },
                      },
                    }}
                  >
                    {brokers.map(broker => (
                      <MenuItem key={broker.id} value={broker.id}>
                        {`${broker.id} - ${broker.brokerName}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Broker Name"
                  value={selectedBroker ? selectedBroker.brokerName || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      brokerName: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                >
                  <InputLabel
                    sx={{
                      fontSize: '11px',
                    }}
                  >
                    Broker Status
                  </InputLabel>
                  <Select
                    value={selectedBroker ? selectedBroker.brokerStatus || false : false}
                    label="Broker Status"
                    onChange={e =>
                      setSelectedBroker({
                        ...selectedBroker,
                        brokerStatus: e.target.value,
                      })
                    }
                    disabled={!selectedBroker}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: '200px',
                          minHeight: '100px',
                          '& .MuiMenuItem-root': {
                            fontSize: '10px',
                            marginLeft: '5px',
                            padding: '5px 15px',
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Primary Contact"
                  value={selectedBroker ? selectedBroker.primaryContact || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      primaryContact: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              {/* <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Secondary Contact"
                  value={selectedBroker ? selectedBroker.secondaryContact || '' : ''}
                  onChange={(e) =>
                    setSelectedBroker({
                      ...selectedBroker,
                      secondaryContact: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-30%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid> */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Primary Email"
                  value={selectedBroker ? selectedBroker.primaryEmail || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      primaryEmail: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              {/* <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Secondary Email"
                  value={selectedBroker ? selectedBroker.secondaryEmail || '' : ''}
                  onChange={(e) =>
                    setSelectedBroker({
                      ...selectedBroker,
                      secondaryEmail: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-30%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid> */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Primary Contact Number"
                  value={selectedBroker ? selectedBroker.primaryContactNumber || '+1' : '+1'}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      primaryContactNumber: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              {/* <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Secondary Contact Number"
                  value={selectedBroker ? selectedBroker.secondaryContactNumber || '+1' : '+1'}
                  onChange={(e) =>
                    setSelectedBroker({
                      ...selectedBroker,
                      secondaryContactNumber: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-30%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid> */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Unit Number"
                  value={selectedBroker ? selectedBroker.unitNumber || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      unitNumber: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Street Number"
                  value={selectedBroker ? selectedBroker.streetNumber || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      streetNumber: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Street Name"
                  value={selectedBroker ? selectedBroker.streetName || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      streetName: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  value={selectedBroker ? selectedBroker.city || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      city: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Province"
                  value={selectedBroker ? selectedBroker.province || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      province: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Country"
                  value={selectedBroker ? selectedBroker.country || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      country: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Postal Code"
                  value={selectedBroker ? selectedBroker.postalCode || '' : ''}
                  onChange={e =>
                    setSelectedBroker({
                      ...selectedBroker,
                      postalCode: e.target.value,
                    })
                  }
                  disabled={!selectedBroker}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 22,
                      minHeight: 22,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '11px',
                      top: '-10%', // Initially center the label vertically
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} align="center">
                <Button variant="contained" onClick={handleUpdateSubmit} sx={{ mt: 2, backgroundColor: '#31478F', fontSize: '10px' }}>
                  Update Broker
                </Button>
                <Button variant="outlined" onClick={() => setUpdateFormVisible(false)} size="small" sx={{ mt: 2, ml: 2, backgroundColor: '#E8F0FE' }}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
          {/* </Card> */}
        </Box>
      )}

      {doNotPerformCustomerFiltration && !formVisible && !updateFormVisible && (
        <TableContainer sx={{ maxWidth: '100%', mb: 2 }}>
          <Table sx={{ maxWidth: '100%' }}>
            <TableHead sx={{ maxWidth: '100%' }}>
              <TableRow sx={{ backgroundColor: '#31478F' }}>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '4px',
                    fontSize: '9px',
                  }}
                >
                  BROKER
                </TableCell>
                {/* <TableCell
                  sx={{
                    color: 'white',
                    padding: '4px',
                    fontSize: '9px',
                  }}
                >
                  TOTAL VOLUMES (GALLONS)
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '4px',
                    fontSize: '9px',
                  }}
                >
                  TOTAL COMMISSION VOLUMES (GALLONS)
                </TableCell> */}
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '4px',
                    fontSize: '9px',
                  }}
                >
                  TOTAL VOLUMES (LITRES)
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '4px',
                    fontSize: '9px',
                  }}
                >
                  TOTAL COMMISSION VOLUMES (LITRES)
                </TableCell>
                {/* <TableCell
                  sx={{
                    color: 'white',
                    padding: '4px',
                    fontSize: '9px',
                  }}
                >
                  EARNED COMMISSION (USD)
                </TableCell> */}
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '4px',
                    fontSize: '9px',
                  }}
                >
                  EARNED COMMISSION (CAD)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ backgroundColor: '#E6F0FF' }}>
                <TableCell sx={{ padding: '2px 8px', fontSize: '8px' }}>ALL BROKERS</TableCell>
                {/* <TableCell sx={{ padding: '2px 8px', fontSize: '8px' }}>
                  {aggregateAllBrokersMetrics().totalMonthlyVolume.toFixed(3)}
                </TableCell>
                <TableCell sx={{ padding: '2px 8px', fontSize: '8px' }}>
                  {aggregateAllBrokersMetrics().totalMonthlyCommission.toFixed(3)}
                </TableCell> */}
                <TableCell sx={{ padding: '2px 8px', fontSize: '8px' }}>{aggregateAllBrokersMetrics().totalVolumeLitres.toFixed(3)}</TableCell>
                <TableCell sx={{ padding: '2px 8px', fontSize: '8px' }}>{aggregateAllBrokersMetrics().totalCommission.toFixed(3)}</TableCell>
                {/* <TableCell sx={{ padding: '2px 8px', fontSize: '8px' }}>
                  {aggregateAllBrokersMetrics().earnedCommissionUSD.toFixed(2)}
                </TableCell> */}
                <TableCell sx={{ padding: '2px 8px', fontSize: '8px' }}>{aggregateAllBrokersMetrics().earnedCommissionCAD.toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Table - shown only when neither form is visible */}
      {!formVisible && !updateFormVisible && (
        <TableContainer sx={{ maxWidth: '100%' }}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <ButtonGroup variant="outlined" aria-label="broker filter button group">
              <Button variant={brokerFilterType === 'currently' ? 'contained' : 'outlined'} onClick={() => setBrokerFilterType('currently')} sx={{ fontSize: '10px', padding: '2px 8px' }}>
                Currently
              </Button>
              <Button variant={brokerFilterType === 'weekly' ? 'contained' : 'outlined'} onClick={() => setBrokerFilterType('weekly')} sx={{ fontSize: '10px', padding: '2px 8px' }}>
                Weekly
              </Button>
              <Button variant={brokerFilterType === 'monthly' ? 'contained' : 'outlined'} onClick={() => setBrokerFilterType('monthly')} sx={{ fontSize: '10px', padding: '2px 8px' }}>
                Monthly
              </Button>
              <Button variant={brokerFilterType === 'yearly' ? 'contained' : 'outlined'} onClick={() => setBrokerFilterType('yearly')} sx={{ fontSize: '10px', padding: '2px 8px' }}>
                Yearly
              </Button>
            </ButtonGroup>
          </Box>
          <Table sx={{ maxWidth: '100%' }}>
            <TableHead sx={{ maxWidth: '100%' }}>
              <TableRow sx={{ backgroundColor: '#31478F' }}>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  BROKER ID
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  BROKER NAME
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  BROKER STATUS
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  PRIMARY EMAILID
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  TOTAL VOLUMES (LITRES)
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  TOTAL COMMISSION VOLUMES (LITRES)
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  EARNED COMMISSION (CAD)
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                >
                  ACTIONS
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    padding: '2px 4px',
                    // whiteSpace: 'nowrap',
                    fontSize: '9px',
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                '& .main-table-row-cell': {
                  color: 'black',
                  fontSize: '9px',
                  padding: '0px',
                  height: '0px',
                },
                '& .MuiTableCell-root': {
                  padding: '0px 4px',
                  margin: '1px',
                  fontSize: '9px',
                  textTransform: 'uppercase',
                },
              }}
            >
              {paginatedBrokers.length > 0 ? (
                [...paginatedBrokers]
                  .sort((a, b) => a.id.localeCompare(b.id)) // If `id` is a string. Use (a.id - b.id) if numeric.
                  .map((broker, index) => {
                    // Compute metrics for the current broker
                    const metrics = aggregateBrokerMetrics(broker.id);
                    return (
                      <React.Fragment key={broker.id}>
                        <TableRow
                          sx={{
                            backgroundColor: index % 2 === 0 ? '#E6F0FF' : '#F8F8F8',
                            height: '8px',
                            '& .main-table-row-cell': {
                              color: 'black',
                              fontSize: '7px',
                              padding: '2px 80px',
                              // height: '7px',
                              // lineHeight: '5px',
                              // borderCollapse: 'collapse',
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            {broker.id}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            {broker.brokerName}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            {broker.brokerStatus ? 'Active' : 'Inactive'}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            {broker.primaryEmail}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            {metrics.totalVolumeLitres.toFixed(3)}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            {metrics.totalCommission.toFixed(3)}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            {metrics.earnedCommissionCAD.toFixed(2)}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            <IconButton onClick={() => handleDelete(broker)}>
                              <DeleteIcon style={{ fontSize: '14px', color: 'black' }} />
                            </IconButton>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '2px 4px',
                              // whiteSpace: 'nowrap',
                              fontSize: '7px',
                            }}
                          >
                            <IconButton onClick={() => handleExpandClick(broker.id)} color="primary">
                              {expandedBroker[broker.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={16}>
                            <Collapse in={expandedBroker[broker.id]} timeout="auto" unmountOnExit>
                              <CardContent>
                                <Typography
                                  variant="h5"
                                  mt={1}
                                  mb={1}
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    fontFamily: 'sans-serif',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  Clients for {broker.brokerName}
                                </Typography>

                                <Box display="flex" justifyContent="flex-end" mb={1}>
                                  <ButtonGroup variant="outlined" aria-label="client filter button group">
                                    <Button
                                      variant={brokerFilterType === 'currently' ? 'contained' : 'outlined'} // Use brokerFilterType
                                      onClick={() => setBrokerFilterType('currently')} // Use setBrokerFilterType
                                      sx={{ fontSize: '10px', padding: '2px 8px' }}
                                    >
                                      Currently
                                    </Button>
                                    <Button
                                      variant={brokerFilterType === 'weekly' ? 'contained' : 'outlined'} // Use brokerFilterType
                                      onClick={() => setBrokerFilterType('weekly')} // Use setBrokerFilterType
                                      sx={{ fontSize: '10px', padding: '2px 8px' }}
                                    >
                                      Weekly
                                    </Button>
                                    <Button
                                      variant={brokerFilterType === 'monthly' ? 'contained' : 'outlined'} // Use brokerFilterType
                                      onClick={() => setBrokerFilterType('monthly')} // Use setBrokerFilterType
                                      sx={{ fontSize: '10px', padding: '2px 8px' }}
                                    >
                                      Monthly
                                    </Button>
                                    <Button
                                      variant={brokerFilterType === 'yearly' ? 'contained' : 'outlined'} // Use brokerFilterType
                                      onClick={() => setBrokerFilterType('yearly')} // Use setBrokerFilterType
                                      sx={{ fontSize: '10px', padding: '2px 8px' }}
                                    >
                                      Yearly
                                    </Button>
                                  </ButtonGroup>
                                </Box>
                                <Table sx={{ maxWidth: '150%' }}>
                                  <TableHead sx={{ maxWidth: '150%' }}>
                                    <TableRow sx={{ backgroundColor: '#005792' }}>
                                      <TableCell sx={{ color: 'white', padding: '5px', fontSize: '10px' }}>Client ID</TableCell>
                                      <TableCell sx={{ color: 'white', padding: '5px', fontSize: '10px' }}>Client Name</TableCell>
                                      <TableCell sx={{ color: 'white', padding: '4px', fontSize: '10px' }}>Total Volumes (Litres)</TableCell>
                                      <TableCell sx={{ color: 'white', padding: '4px', fontSize: '10px' }}>Total Commission Volumes (Litres)</TableCell>
                                      <TableCell sx={{ color: 'white', padding: '4px', fontSize: '10px' }}>Earned Commission (CAD)</TableCell>
                                      <TableCell sx={{ color: 'white', padding: '4px', fontSize: '10px' }}>Actions</TableCell>
                                      <TableCell sx={{ color: 'white', padding: '4px', fontSize: '10px' }}></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody
                                    sx={{
                                      '& .main-table-row-cell': {
                                        color: 'black',
                                        fontSize: '10px',
                                        padding: '0px',
                                        height: '0px',
                                      },
                                      '& .MuiTableCell-root': {
                                        padding: '0px 6px',
                                        margin: '1px',
                                        fontSize: '9px',
                                      },
                                    }}
                                  >
                                    {newSelectedClient[broker.id] && newSelectedClient[broker.id].length > 0 ? (
                                      newSelectedClient[broker.id].map((client, index) => {
                                        const clientData = brokerData[client.clientID] || {};
                                        // const GALLONS_TO_LITRES = 3.78541;
                                        const currentDate = new Date(); // June 11, 2025
                                        const effectiveDate = client.effectiveDate ? new Date(client.effectiveDate) : null;

                                        // Log raw client data for debugging
                                        console.log(`Client ${client.clientID} Data:`, {
                                          currentVolume: clientData.currentVolume,
                                          currentCommission: clientData.currentCommission,
                                          weeklyVolume: clientData.weeklyVolume,
                                          weeklyCommission: clientData.weeklyCommission,
                                          monthlyVolume: clientData.monthlyVolume,
                                          monthlyCommission: clientData.monthlyCommission,
                                          yearlyVolume: clientData.yearlyVolume,
                                          yearlyCommission: clientData.yearlyCommission,
                                          totalVolume: clientData.totalVolume,
                                          totalCommission: clientData.totalCommission,
                                          effectiveDate: client.effectiveDate,
                                          commission: client.commission,
                                        });

                                        // Initialize metrics
                                        let totalVolume = 0;
                                        let totalCommission = 0;
                                        let earnedCommissionCAD = 0;

                                        // Normalize volume and commission (convert to gallons if in litres)
                                        const parseVolume = value => {
                                          const num = parseFloat(value) || 0;
                                          // if (num > 1000) {
                                          //   return num / GALLONS_TO_LITRES; // Convert litres to gallons
                                          // }
                                          return num; // Already in gallons
                                        };

                                        if (effectiveDate) {
                                          // Define time boundaries
                                          const truncateToDate = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                          const dayOfWeek = currentDate.getDay();
                                          const currentWeekStart = new Date(currentDate);
                                          currentWeekStart.setDate(currentDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
                                          const currentWeekEnd = new Date(currentWeekStart);
                                          currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
                                          const lastWeekStart = new Date(currentWeekStart);
                                          lastWeekStart.setDate(currentWeekStart.getDate() - 7);
                                          const lastWeekEnd = new Date(currentWeekStart);
                                          lastWeekEnd.setDate(currentWeekStart.getDate() - 1);

                                          // Apply filter logic
                                          switch (brokerFilterType) {
                                            case 'currently':
                                              if (truncateToDate(effectiveDate) <= currentWeekEnd) {
                                                totalVolume = parseVolume(clientData.currentVolume);
                                                totalCommission = parseVolume(clientData.currentCommission) || totalVolume;
                                              }
                                              break;
                                            case 'weekly':
                                              if (truncateToDate(effectiveDate) <= lastWeekEnd) {
                                                totalVolume = parseVolume(clientData.weeklyVolume);
                                                totalCommission = parseVolume(clientData.weeklyCommission) || totalVolume;
                                              }
                                              break;
                                            case 'monthly':
                                              if (effectiveDate.getFullYear() <= currentDate.getFullYear()) {
                                                totalVolume = parseVolume(clientData.monthlyVolume);
                                                totalCommission = parseVolume(clientData.monthlyCommission) || totalVolume;
                                                // If monthly data is 0, approximate using weeklyVolume * 4
                                                if (totalVolume === 0) {
                                                  totalVolume = parseVolume(clientData.weeklyVolume) * 4;
                                                  totalCommission = totalVolume;
                                                }
                                              }
                                              break;
                                            case 'yearly':
                                              if (effectiveDate.getFullYear() <= currentDate.getFullYear()) {
                                                totalVolume = parseVolume(clientData.yearlyVolume);
                                                totalCommission = parseVolume(clientData.yearlyCommission) || totalVolume;
                                                if (totalVolume === 0) {
                                                  // Use monthlyVolume if available, otherwise approximate from weeklyVolume
                                                  const monthlyVol = parseVolume(clientData.monthlyVolume) || parseVolume(clientData.weeklyVolume) * 4;
                                                  totalVolume = monthlyVol * 12;
                                                  totalCommission = totalVolume;
                                                }
                                              }
                                              break;
                                            default:
                                              totalVolume = parseVolume(clientData.totalVolume);
                                              totalCommission = parseVolume(clientData.totalCommission) || totalVolume;
                                              break;
                                          }
                                        }

                                        // Convert gallons to liters
                                        const totalVolumeLitres = totalVolume;
                                        const totalCommissionLitres = totalCommission;

                                        // Calculate earned commission based on totalCommissionLitres
                                        earnedCommissionCAD = totalCommissionLitres * (client.commission || 0);

                                        // Log computed values
                                        console.log(`Client ${client.clientID} Computed:`, {
                                          filterType: clientFilterType,
                                          totalVolume,
                                          totalCommission,
                                          totalVolumeLitres,
                                          totalCommissionLitres,
                                          commission: client.commission,
                                          earnedCommissionCAD,
                                        });

                                        return (
                                          <React.Fragment key={client.clientID}>
                                            <TableRow
                                              sx={{
                                                backgroundColor: index % 2 === 0 ? '#D6E6F2' : '#EDF6FF',
                                              }}
                                            >
                                              <TableCell>{client.clientID}</TableCell>
                                              <TableCell>{client.clientName}</TableCell>
                                              <TableCell>{totalVolumeLitres.toFixed(3)}</TableCell>
                                              <TableCell>{totalCommissionLitres.toFixed(3)}</TableCell>
                                              <TableCell>{earnedCommissionCAD.toFixed(3)}</TableCell>
                                              <TableCell>
                                                <IconButton onClick={() => handleClientDelete(broker.id, client.clientID)}>
                                                  <DeleteIcon sx={{ fontSize: '14px', color: 'black' }} />
                                                </IconButton>
                                              </TableCell>
                                              <TableCell>
                                                <IconButton onClick={() => handleTransactionExpandClick(broker.id, client.clientID)} color="primary">
                                                  {transactionData[client.clientID] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                              </TableCell>
                                            </TableRow>

                                            <TableRow>
                                              <TableCell colSpan={11}>
                                                <Collapse in={transactionData[client.clientID]} timeout="auto" unmountOnExit>
                                                  <CardContent>
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>Transaction Details for {client.clientName}</Typography>
                                                    <Box display="flex" justifyContent="flex-end">
                                                      <ButtonGroup variant="outlined" aria-label="outlined button group"></ButtonGroup>
                                                    </Box>
                                                    {transactionData[client.clientID] && (
                                                      <TransactionTable
                                                        transactions={transactionData[client.clientID]}
                                                        filterType={brokerFilterType} // Replace clientFilterType with brokerFilterType
                                                        client={client}
                                                      />
                                                    )}
                                                  </CardContent>
                                                </Collapse>
                                              </TableCell>
                                            </TableRow>
                                          </React.Fragment>
                                        );
                                      })
                                    ) : (
                                      <TableRow>
                                        <TableCell colSpan={11} align="center">
                                          No clients available
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </CardContent>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No brokers available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination - shown only when neither form is visible */}
      {!formVisible && !updateFormVisible && <TablePagination rowsPerPageOptions={[10]} component="div" count={brokers.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />}

      {/* <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity='success'>
          {snackbarMessage}
        </Alert>
      </Snackbar> */}

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this broker?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Rest of the code (RowCAD and TransactionTable) remains unchanged
const RowCAD = ({ row, index, doNotPerformCustomerFiltration }) => {
  const [open, setOpen] = useState(false);

  // // Skip this row if any product is "DEF" or "DEFD"
  // const hasExcludedProduct = row?.others?.some(
  //   (otherRow) => otherRow.product === 'DEF' || otherRow.product === 'DEFD'
  // );

  // if (hasExcludedProduct) {
  //   return null; // Skip rendering this row entirely
  // }

  return (
    <React.Fragment>
      <TableRow
        // sx={{ '& > *': { borderBottom: 'unset', padding: '8px 6px' } }}
        sx={{
          '& .main-table-row-cell': {
            color: 'black',
            fontSize: '10px',
            padding: '0px',
            height: '0px',
          },
          '& .MuiTableCell-root': {
            padding: '8px 6px',
            margin: '1px',
            fontSize: '9px',
          },
        }}
        className={index % 2 === 0 ? 'main-table-row-even' : 'main-table-row-odd'}
      >
        {/* <TableCell>
          <IconButton aria-label='expand row' size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell> */}
        {doNotPerformCustomerFiltration && <TableCell className="main-table-row-cell">{row.companyXRef}</TableCell>}
        <TableCell className="main-table-row-cell">{row.transactionDate}</TableCell>
        <TableCell className="main-table-row-cell">{row.cardNumber?.slice(-5)}</TableCell>
        {/* <TableCell className='main-table-row-cell'>{row.driverName}</TableCell> */}
        {/* <TableCell className='main-table-row-cell'>{row.locationName}</TableCell> */}
        <TableCell className="main-table-row-cell">{row.locationCity}</TableCell>
        <TableCell className="main-table-row-cell">{row.locationState}</TableCell>
        <TableCell className="main-table-row-cell">{row.unitNumber}</TableCell>
        {row?.others?.map(otherRow => (
          <>
            <TableCell className="main-table-row-cell">{otherRow.product}</TableCell>
            <TableCell className="main-table-row-cell">{otherRow.qty}</TableCell>
          </>
        ))}
      </TableRow>
    </React.Fragment>
  );
};

const TransactionTable = ({ transactions, doNotPerformCustomerFiltration, client }) => {
  const [filterType, setFilterType] = useState('currently');
  const clientId = client.clientID;

  const filterTransactions = (transactions, filterType) => {
    const currentDate = new Date();

    // Helper to truncate time (compare only dates)
    const truncateToDate = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Get current day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = currentDate.getDay();

    // Calculate start of current week (Monday)
    const currentWeekStart = new Date(currentDate);
    currentWeekStart.setDate(currentDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    // Calculate end of current week (Sunday)
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);

    // Last week range
    const lastWeekStart = new Date(currentWeekStart);
    lastWeekStart.setDate(currentWeekStart.getDate() - 7);

    const lastWeekEnd = new Date(currentWeekStart);
    lastWeekEnd.setDate(currentWeekStart.getDate() - 1);

    // Get effective date for client
    const getEffectiveDateForClient = clientId => {
      return client?.effectiveDate ? truncateToDate(new Date(client.effectiveDate)) : null;
    };

    return transactions
      .filter(transaction => {
        // Skip DEF/DEFD products
        const hasDefProduct = transaction.lineItems?.some(item => item.category === 'DEF' || item.category === 'DEFD');
        return !hasDefProduct;
      })
      .sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate))
      .filter(transaction => {
        const transactionDate = truncateToDate(new Date(transaction.transactionDate));
        const effective = getEffectiveDateForClient(transaction.companyXRef);
        const effectiveDate = effective ? new Date(effective.setDate(effective.getDate() + 1)) : null;

        if (!effectiveDate) return false;

        switch (filterType) {
          case 'currently':
            return transactionDate >= effectiveDate && transactionDate >= truncateToDate(currentWeekStart) && transactionDate <= truncateToDate(currentWeekEnd);
          case 'weekly':
            return transactionDate >= effectiveDate && transactionDate >= truncateToDate(lastWeekStart) && transactionDate <= truncateToDate(lastWeekEnd);
          case 'monthly':
            return transactionDate >= effectiveDate && transactionDate.getFullYear() === currentDate.getFullYear() && transactionDate.getMonth() === currentDate.getMonth();
          case 'yearly':
            return transactionDate >= effectiveDate && transactionDate.getFullYear() === currentDate.getFullYear();
          default:
            return transactionDate >= effectiveDate;
        }
      });
  };

  const processTransactionDetails = transactionDetail => {
    let others = [];
    if (transactionDetail && transactionDetail.lineItems) {
      const ulsdLineItem = transactionDetail.lineItems.find(lineItem => lineItem.category === 'ULSD');
      const defdLineItem = transactionDetail.lineItems.find(lineItem => lineItem.category === 'DEFD');
      const rfrLineItem = transactionDetail.lineItems.find(lineItem => lineItem.category === 'RFR');

      if (ulsdLineItem) {
        const amount = doNotPerformCustomerFiltration ? ulsdLineItem.amount || 0 : transactionDetail.customerAmount || 0;
        const customerAmount = transactionDetail.customerAmount || 0;
        others.push({
          product: 'DIESEL',
          qty: (ulsdLineItem.quantity || 0).toFixed(3),
          retailAmount: (ulsdLineItem.retailAmount || 0).toFixed(3),
          retailPPU: (ulsdLineItem.retailAmount / ulsdLineItem.quantity || 0).toFixed(3),
          ppu: doNotPerformCustomerFiltration ? (ulsdLineItem.ppu || 0).toFixed(3) : (transactionDetail.customerPPU || 0).toFixed(3),
          gstHst: doNotPerformCustomerFiltration ? (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'HST' && tax.grossNetFlag === 'N')?.amount || 0).toFixed(3) : (transactionDetail.customerGsthst || 0).toFixed(3),
          pst: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'PST')?.amount || 0).toFixed(3),
          fet: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'FET')?.amount || 0).toFixed(3),
          pft: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'PFT')?.amount || 0).toFixed(3),
          mft: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'MFT')?.amount || 0).toFixed(3),
          ct: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'PCT')?.amount || 0).toFixed(3),
          savings: doNotPerformCustomerFiltration ? (ulsdLineItem.discAmount || 0).toFixed(3) : (transactionDetail.customerSavings || 0).toFixed(3),
          amount: doNotPerformCustomerFiltration ? (ulsdLineItem.amount || 0).toFixed(3) : (transactionDetail.customerAmount || 0).toFixed(3),
          customerPpu: (transactionDetail.customerPPU || 0).toFixed(3),
          customerGstHst: (transactionDetail.customerGsthst || 0).toFixed(3),
          customerPst: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'PST')?.amount || 0).toFixed(3),
          customerFet: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'FET')?.amount || 0).toFixed(3),
          customerPft: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'PFT')?.amount || 0).toFixed(3),
          customerMft: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'MFT')?.amount || 0).toFixed(3),
          customerCt: (ulsdLineItem.lineTaxes.find(tax => tax.taxCode === 'PCT')?.amount || 0).toFixed(3),
          customerSavings: (transactionDetail.customerSavings || 0).toFixed(3),
          customerAmount: (transactionDetail.customerAmount || 0).toFixed(3),
          ksgProfit: (customerAmount - amount || 0).toFixed(3),
        });
      } else if (defdLineItem) {
        const amount = defdLineItem.amount.toFixed(3) || 0;
        const customerAmount = defdLineItem.amount.toFixed(3) || 0;
        others.push({
          product: 'DEF',
          qty: (defdLineItem.quantity || 0).toFixed(3),
          retailAmount: (defdLineItem.retailAmount || 0).toFixed(3),
          retailPPU: (defdLineItem.retailPPU || 0).toFixed(3),
          ppu: (defdLineItem.ppu || 0).toFixed(3),
          gstHst: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'HST' && tax.grossNetFlag === 'N')?.amount || 0).toFixed(3),
          pst: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'PST')?.amount || 0).toFixed(3),
          fet: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'FET')?.amount || 0).toFixed(3),
          pft: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'PFT')?.amount || 0).toFixed(3),
          mft: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'MFT')?.amount || 0).toFixed(3),
          ct: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'PCT')?.amount || 0).toFixed(3),
          savings: (defdLineItem.discAmount || 0).toFixed(3),
          amount: (defdLineItem.amount || 0).toFixed(3),
          customerPpu: (defdLineItem.ppu || 0).toFixed(3),
          customerGstHst: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'HST' && tax.grossNetFlag === 'N')?.amount || 0).toFixed(3),
          customerPst: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'PST')?.amount || 0).toFixed(3),
          customerFet: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'FET')?.amount || 0).toFixed(3),
          customerPft: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'PFT')?.amount || 0).toFixed(3),
          customerMft: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'MFT')?.amount || 0).toFixed(3),
          customerCt: (defdLineItem.lineTaxes.find(tax => tax.taxCode === 'PCT')?.amount || 0).toFixed(3),
          customerSavings: (defdLineItem.discAmount || 0).toFixed(3),
          customerAmount: (defdLineItem.amount || 0).toFixed(3),
          ksgProfit: (customerAmount - amount || 0).toFixed(3),
        });
      } else if (rfrLineItem) {
        const amount = doNotPerformCustomerFiltration ? rfrLineItem.amount || 0 : transactionDetail.customerAmount || 0;
        const customerAmount = transactionDetail.customerAmount || 0;
        others.push({
          product: 'RFR',
          qty: (rfrLineItem.quantity || 0).toFixed(3),
          retailAmount: (rfrLineItem.retailAmount || 0).toFixed(3),
          retailPPU: (rfrLineItem.retailAmount / rfrLineItem.quantity || 0).toFixed(3),
          ppu: doNotPerformCustomerFiltration ? (rfrLineItem.ppu || 0).toFixed(3) : (transactionDetail.customerPPU || 0).toFixed(3),
          gstHst: doNotPerformCustomerFiltration ? (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'HST' && tax.grossNetFlag === 'N')?.amount || 0).toFixed(3) : (transactionDetail.customerGsthst || 0).toFixed(3),
          pst: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'PST')?.amount || 0).toFixed(3),
          fet: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'FET')?.amount || 0).toFixed(3),
          pft: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'PFT')?.amount || 0).toFixed(3),
          mft: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'MFT')?.amount || 0).toFixed(3),
          ct: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'PCT')?.amount || 0).toFixed(3),
          savings: doNotPerformCustomerFiltration ? (rfrLineItem.discAmount || 0).toFixed(3) : (transactionDetail.customerSavings || 0).toFixed(3),
          amount: doNotPerformCustomerFiltration ? (rfrLineItem.amount || 0).toFixed(3) : (transactionDetail.customerAmount || 0).toFixed(3),
          customerPpu: (transactionDetail.customerPPU || 0).toFixed(3),
          customerGstHst: (transactionDetail.customerGsthst || 0).toFixed(3),
          customerPst: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'PST')?.amount || 0).toFixed(3),
          customerFet: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'FET')?.amount || 0).toFixed(3),
          customerPft: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'PFT')?.amount || 0).toFixed(3),
          customerMft: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'MFT')?.amount || 0).toFixed(3),
          customerCt: (rfrLineItem.lineTaxes.find(tax => tax.taxCode === 'PCT')?.amount || 0).toFixed(3),
          customerSavings: (transactionDetail.customerSavings || 0).toFixed(3),
          customerAmount: (transactionDetail.customerAmount || 0).toFixed(3),
          ksgProfit: (customerAmount - amount || 0).toFixed(3),
        });
      }
    }
    return {
      companyXRef: doNotPerformCustomerFiltration ? transactionDetail.companyXRef : '',
      transactionDate: `${transactionDetail?.transactionDate?.slice(0, 10)}`,
      cardNumber: transactionDetail?.cardNumber?.slice(-5),
      driverName: transactionDetail.infos.find(info => info.type === 'NAME')?.value || '',
      locationName: transactionDetail?.locationName,
      locationCity: transactionDetail?.locationCity,
      locationState: transactionDetail?.locationState,
      unitNumber: transactionDetail.infos.find(info => info.type === 'UNIT')?.value || '',
      others: others,
    };
  };

  const filteredTransactions = filterTransactions(transactions, filterType);

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ButtonGroup
          sx={{
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #007bff', // Single border for the group
          }}
        >
          {['currently', 'weekly', 'monthly', 'yearly'].map(type => (
            <Button
              key={type}
              onClick={() => setFilterType(type)}
              sx={{
                textTransform: 'none',
                fontSize: '10px',
                fontWeight: 'bold',
                minWidth: '50px',
                padding: '2px 6px', // Adjusted padding for a smaller button
                borderRadius: '4px', // Smaller rounded corners

                color: filterType === type ? '#fff' : '#007bff',
                backgroundColor: filterType === type ? '#007bff' : 'transparent',
                border: '1px solid #007bff',
                '&:hover': {
                  backgroundColor: filterType === type ? '#0056b3' : 'rgba(0, 123, 255, 0.1)',
                  borderColor: '#0056b3',
                },
              }}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      <Table sx={{ maxWidth: '100%', padding: '4px' }}>
        <TableHead sx={{ whiteSpace: 'prewrap' }}>
          <TableRow sx={{ backgroundColor: '#31478F' }}>
            {/* <TableCell sx={{ color: 'white' }}></TableCell> */}
            {doNotPerformCustomerFiltration && <TableCell sx={{ color: 'white' }}>Company XRef</TableCell>}
            <TableCell sx={{ color: 'white' }}>Transaction Date & Time</TableCell>
            <TableCell sx={{ color: 'white' }}>Card Number</TableCell>
            {/* <TableCell sx={{ color: 'white' }}>Driver Name</TableCell> */}
            {/* <TableCell sx={{ color: 'white' }}>Location Name</TableCell> */}
            <TableCell sx={{ color: 'white' }}>Location City</TableCell>
            <TableCell sx={{ color: 'white' }}>Location State</TableCell>
            <TableCell sx={{ color: 'white' }}>Unit Number</TableCell>
            <TableCell sx={{ color: 'white' }}>PRODUCT</TableCell>
            <TableCell sx={{ color: 'white' }}>QTY</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTransactions.length > 0 ? (
            filteredTransactions.map((transaction, index) => <RowCAD key={transaction.transactionNumber} row={processTransactionDetails(transaction)} index={index} doNotPerformCustomerFiltration={doNotPerformCustomerFiltration} />)
          ) : (
            <TableRow>
              <TableCell colSpan={9} align="center">
                No transactions available for {filterType}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

TransactionTable.propTypes = {
  transactions: PropTypes.array.isRequired,
  filterType: PropTypes.string.isRequired,
  client: PropTypes.object.isRequired,
};

export default BrokerList;
